import type { ButtonVariants } from "@/components/ui/baseUi/button"
import { Button } from "@/components/ui/baseUi/button"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { type StripeAccountErrorItemType } from "@modules/finances/components/stripeAccountErrorAlert"
import {
  StripeErrorMissingDocumentButton,
  StripeErrorProgramBundleCardSettingsButton,
  StripeErrorProgramCardSettingsButton,
} from "@modules/finances/components/stripeErrorButtons"
import { PayoutEnumApi } from "@modules/finances/financesPayout/gateway/payoutApi"
import { type PaymentError, StripeErrorCode } from "@modules/finances/types/stripeErrorTypes"
import { getStripeAccountErrorText } from "@modules/finances/utils/financeUtils"
import { useProgramBundleSidebar } from "@modules/programBundle/hooks/useProgramBundleSidebar"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo } from "react"

export const useStripeAccountErrors = (
  payoutEnumApi: PayoutEnumApi,
  buttonSize?: ButtonVariants["size"],
  enabled: boolean = true,
) => {
  const { userFinanceApi, boxFinanceApi } = useServicesContext()
  const financeApi = payoutEnumApi === PayoutEnumApi.OWNER ? boxFinanceApi : userFinanceApi

  const { data: stripeAccountErrors, isLoading } = useQuery({
    queryKey: [payoutEnumApi === PayoutEnumApi.OWNER ? "boxStripeAccountErrors" : "userStripeAccountErrors"],
    queryFn: () => financeApi.getStripeAccountErrors(),
    staleTime: 0,
    enabled,
  })

  const { navigateToProgramView } = useProgramSidebar()
  const { navigateToProgramBundleResume } = useProgramBundleSidebar()
  const queryClient = useQueryClient()

  useEffect(() => {
    // Set staleTime to 5min if there's no error
    if (
      !isLoading &&
      (!stripeAccountErrors ||
        (!stripeAccountErrors.accountError &&
          (!stripeAccountErrors.paymentErrors || stripeAccountErrors.paymentErrors.length === 0)))
    ) {
      queryClient.setQueryDefaults(
        [payoutEnumApi === PayoutEnumApi.OWNER ? "boxStripeAccountErrors" : "userStripeAccountErrors"],
        { staleTime: 10 * 60 * 1000 },
      ) // 10 min
    }
  }, [stripeAccountErrors, isLoading])

  const openProgramDetails = (programId: string) => {
    // Payment errors can only occur on subscription, so only for infinite programs
    navigateToProgramView(programId, false)
  }

  const openProgramBundleDetails = (programBundleId: string) => {
    navigateToProgramBundleResume(programBundleId)
  }

  const formattedStripeAccountErrors = useMemo(() => {
    const errors: StripeAccountErrorItemType[] = []

    if (stripeAccountErrors?.accountError) {
      errors.push({
        ...getStripeAccountErrorText(stripeAccountErrors.accountError),
        primaryButton: (
          <StripeErrorMissingDocumentButton
            accountErrorCode={stripeAccountErrors.accountError}
            openBankingTab={payoutEnumApi === PayoutEnumApi.OWNER}
          />
        ),
      })
    }

    stripeAccountErrors?.paymentErrors.forEach((paymentError) => {
      errors.push({
        alertTitle: word("global.stripeError.paymentProblem.title"),
        alertSubtitle: getPaymentErrorSubtitle(paymentError),
        alertContent: getPaymentErrorContent(paymentError, payoutEnumApi === PayoutEnumApi.OWNER),
        primaryButton: paymentError.programId ? (
          <StripeErrorProgramCardSettingsButton
            programId={paymentError.programId}
            paymentError={paymentError}
            buttonSize={buttonSize ?? "sm"}
          />
        ) : paymentError.programBundleId ? (
          <StripeErrorProgramBundleCardSettingsButton
            programBundleId={paymentError.programBundleId}
            paymentError={paymentError}
            buttonSize={buttonSize ?? "sm"}
          />
        ) : undefined,
        secondaryButton:
          paymentError.programId || paymentError.programBundleId ? (
            <Button
              variant="secondary"
              size={buttonSize ?? "sm"}
              className="w-full"
              onClick={() => {
                if (paymentError.programId) openProgramDetails(paymentError.programId)
                if (paymentError.programBundleId) openProgramBundleDetails(paymentError.programBundleId)
              }}
            >
              {word("global.stripeError.paymentProblem.program.buttonLabel")}
            </Button>
          ) : undefined,
      })
    })

    return errors
  }, [stripeAccountErrors, payoutEnumApi, buttonSize, openProgramDetails, openProgramBundleDetails])

  return formattedStripeAccountErrors
}

export function getPaymentErrorSubtitle(paymentError: PaymentError): string {
  switch (paymentError.errorCode) {
    case StripeErrorCode.CARD_DECLINED:
    case StripeErrorCode.DO_NOT_HONOR:
      return word(`global.stripeError.paymentProblem.CARD_DECLINED.label`)
    case StripeErrorCode.EXPIRED_CARD:
    case StripeErrorCode.AUTHENTICATION_REQUIRED:
      return word(`global.stripeError.paymentProblem.${paymentError.errorCode}.label`, {
        subscriptionName: paymentError.subscriptionName,
        cardNumber: paymentError.last4Digits,
      })
    case StripeErrorCode.PAYMENT_INTENT_AUTHENTICATION_FAILURE:
      return word(`global.stripeError.paymentProblem.PAYMENT_INTENT_AUTHENTICATION_FAILURE.label`)
    default:
      return word(`global.stripeError.paymentProblem.fallback.label`, {
        subscriptionName: paymentError.subscriptionName,
        cardNumber: paymentError.last4Digits,
      })
  }
}

export function getPaymentErrorContent(paymentError: PaymentError, lightContent?: boolean): string {
  if (paymentError.boxId && paymentError.boxOfferId) {
    return word(`global.stripeError.paymentProblem.box.content`, {
      subscriptionName: paymentError.subscriptionName,
    })
  }

  switch (paymentError.errorCode) {
    case StripeErrorCode.DO_NOT_HONOR:
    case StripeErrorCode.CARD_DECLINED:
      if (paymentError.last4Digits) {
        return word(`global.stripeError.paymentProblem.CARD_DECLINED.${lightContent ? "lightContent" : "content"}`, {
          subscriptionName: paymentError.subscriptionName,
          cardNumber: paymentError.last4Digits,
        })
      } else {
        return word(
          `global.stripeError.paymentProblem.fallback.${lightContent ? "lightContentNoCardNumber" : "contentNoCardNumber"}`,
          {
            subscriptionName: paymentError.subscriptionName,
          },
        )
      }
    case StripeErrorCode.PAYMENT_INTENT_AUTHENTICATION_FAILURE:
      return word(
        `global.stripeError.paymentProblem.PAYMENT_INTENT_AUTHENTICATION_FAILURE.${lightContent ? "lightContent" : "content"}`,
        {
          subscriptionName: paymentError.subscriptionName,
        },
      )
    case StripeErrorCode.EXPIRED_CARD:
    case StripeErrorCode.AUTHENTICATION_REQUIRED:
      return word(
        `global.stripeError.paymentProblem.${paymentError.errorCode}.${lightContent ? "lightContent" : "content"}`,
        {
          subscriptionName: paymentError.subscriptionName,
          cardNumber: paymentError.last4Digits,
        },
      )
    default:
      if (paymentError.last4Digits) {
        return word(`global.stripeError.paymentProblem.fallback.${lightContent ? "lightContent" : "content"}`, {
          subscriptionName: paymentError.subscriptionName,
          cardNumber: paymentError.last4Digits,
        })
      } else {
        return word(
          `global.stripeError.paymentProblem.fallback.${lightContent ? "lightContentNoCardNumber" : "contentNoCardNumber"}`,
          {
            subscriptionName: paymentError.subscriptionName,
          },
        )
      }
  }
}

export function getPaymentErrorButtonLabel(paymentError: PaymentError): string {
  return paymentError.errorCode === StripeErrorCode.AUTHENTICATION_REQUIRED
    ? word("global.stripeError.paymentProblem.program.authenticationRequired")
    : word("global.stripeError.paymentProblem.program.cardSettings")
}
